import React from 'react';
import type { UserFormValue, UsersMeta } from './types';
import { fieldClass, helperClass, labelClass } from '../styles';

interface UserFormFieldsProps {
  meta: UsersMeta;
  value: UserFormValue;
  onChange: (next: UserFormValue) => void;
  mode: 'create' | 'edit';
  disabled?: boolean;
}

const UserFormFields: React.FC<UserFormFieldsProps> = ({
  meta,
  value,
  onChange,
  mode,
  disabled,
}) => {
  const allowedTypeKeys =
    mode === 'create'
      ? [
          ...(meta.permissions.create_catalyx ? ['catalyx'] : []),
          ...(meta.permissions.create_client ? ['client'] : []),
        ]
      : [
          ...(meta.permissions.edit_catalyx ? ['catalyx'] : []),
          ...(meta.permissions.edit_client ? ['client'] : []),
        ];
  const userTypes = meta.user_types.filter((t) => allowedTypeKeys.includes(t.key));
  const selectedType = meta.user_types.find((t) => t.id === value.user_type_id);
  const typeKey = selectedType?.key;

  const set = (patch: Partial<UserFormValue>) => onChange({ ...value, ...patch });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className={labelClass}>
          Email address <span className="text-red-600">*</span>
        </label>
        <input
          type="email"
          value={value.email}
          disabled={disabled}
          onChange={(e) => set({ email: e.target.value })}
          className={fieldClass}
          autoComplete="off"
        />
      </div>
      <div>
        <label className={labelClass}>First name</label>
        <input
          value={value.first_name}
          disabled={disabled}
          onChange={(e) => set({ first_name: e.target.value })}
          className={fieldClass}
        />
      </div>
      <div>
        <label className={labelClass}>Last name</label>
        <input
          value={value.last_name}
          disabled={disabled}
          onChange={(e) => set({ last_name: e.target.value })}
          className={fieldClass}
        />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>Company</label>
        <input
          value={value.company}
          disabled={disabled}
          onChange={(e) => set({ company: e.target.value })}
          className={fieldClass}
        />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>
          Organization <span className="text-red-600">*</span>
        </label>
        <select
          value={value.organization_id}
          disabled={disabled}
          onChange={(e) =>
            set({ organization_id: e.target.value ? Number(e.target.value) : '' })
          }
          className={fieldClass}
        >
          <option value="">Select an organization</option>
          {meta.organizations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
        <p className={helperClass}>
          If the organization is missing, you need to create the organization first.
        </p>
      </div>
      <div>
        <label className={labelClass}>
          User type <span className="text-red-600">*</span>
        </label>
        <select
          value={value.user_type_id}
          disabled={disabled}
          onChange={(e) => {
            const nextId = e.target.value ? Number(e.target.value) : '';
            const nextType = meta.user_types.find((t) => t.id === nextId);
            set({
              user_type_id: nextId,
              catalyx_role_id: nextType?.key === 'catalyx' ? value.catalyx_role_id : '',
              client_role_id: nextType?.key === 'client' ? value.client_role_id : '',
            });
          }}
          className={fieldClass}
        >
          <option value="">Select a user type</option>
          {userTypes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        {userTypes.length === 0 && (
          <p className={helperClass}>
            You do not have permission to {mode} Catalyx or Client users.
          </p>
        )}
      </div>
      {typeKey === 'catalyx' && (
        <div>
          <label className={labelClass}>
            Catalyx role <span className="text-red-600">*</span>
          </label>
          <select
            value={value.catalyx_role_id}
            disabled={disabled}
            onChange={(e) =>
              set({ catalyx_role_id: e.target.value ? Number(e.target.value) : '' })
            }
            className={fieldClass}
          >
            <option value="">Select a Catalyx role</option>
            {meta.catalyx_roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {typeKey === 'client' && (
        <div>
          <label className={labelClass}>
            Client role <span className="text-red-600">*</span>
          </label>
          <select
            value={value.client_role_id}
            disabled={disabled}
            onChange={(e) =>
              set({ client_role_id: e.target.value ? Number(e.target.value) : '' })
            }
            className={fieldClass}
          >
            <option value="">Select a Client role</option>
            {meta.client_roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default UserFormFields;
